import { useState, useCallback, useMemo, useEffect } from "react";
import { Plus, FileText, Eye, Trash2, Loader2, Download, Copy, CheckCircle, XCircle, Clock, Folder, FolderPlus, Home, Move, ChevronRight, ArrowLeft, Edit2, FolderOpen, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";
import Modal from "../../components/ui/Modal";
import { useModal } from "../../hooks/useModal";
import { ApiService } from "../../services/api";
import { FileItem as ApiFileItem, FileFolder, MaarifTestFileMetadata } from "../../types/file";
import type { QuestionBankQuestion, QuestionChoiceLayout } from "../../types/question-bank";
import { useAuth } from "../../contexts/AuthContext";
import checkPermission from "../../utils/permission";

interface FileItem {
  id: string;
  name: string;
  type: "pdf" | "image" | "maarif_test";
  size: number;
  url: string;
  uploadedAt: string;
  folderId?: string | null;
  metadata?: Record<string, any> | MaarifTestFileMetadata;
}

const MAX_STORAGE = 100 * 1024 * 1024;
const MAX_NOTEBOOK_PAGES = 150;

const FOLDER_COLORS = [
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#14B8A6',
  '#0EA5E9',
  '#6B7280',
];

const isMaarifTestMetadata = (metadata: any): metadata is MaarifTestFileMetadata =>
  Boolean(metadata && metadata.kind === "maarif_test" && metadata.questionBankId);

const mapApiFileToCard = (file: any): FileItem => ({
  id: file.id,
  name: file.originalName || file.name,
  type: isMaarifTestMetadata(file.metadata) ? "maarif_test" : file.mimeType?.startsWith("image/") ? "image" : "pdf",
  size: file.size,
  url: file.url,
  uploadedAt: file.createdAt,
  folderId: file.folderId || null,
  metadata: file.metadata,
});

const BookOpenIcon = ({ small = false }: { small?: boolean }) => (
  <BookOpen className={small ? "h-5 w-5" : "h-10 w-10"} />
);

const applyMaarifTemplateToQuestion = (
  question: QuestionBankQuestion,
  settings: {
    choiceLayout: QuestionChoiceLayout;
    showIntroText: boolean;
  }
): QuestionBankQuestion => {
  const isVisualQuestion = Boolean(question.image) || question.templateType === "image_with_caption";
  const templateType = isVisualQuestion
    ? "image_with_caption"
    : settings.choiceLayout === "horizontal"
      ? "horizontal_choices"
      : "classic_vertical";

  return {
    ...question,
    templateType,
    choiceLayout: settings.choiceLayout,
    showIntroText: settings.showIntroText,
    intro_text: question.intro_text || "",
  };
};

export default function FilesPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();
  const { refreshUserData } = useAuth();

  const canListFiles = checkPermission("file_list");
  const canUploadFile = checkPermission("file_upload");
  const canDeleteFile = checkPermission("file_delete");
  const canProcessFile = checkPermission("file_process");

  const [categories, setCategories] = useState<FileFolder[]>([]);
  const [rootFolders, setRootFolders] = useState<FileFolder[]>([]);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showNotebookModal, setShowNotebookModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMaarifTemplateModal, setShowMaarifTemplateModal] = useState(false);

  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [selectedMaarifFile, setSelectedMaarifFile] = useState<FileItem | ApiFileItem | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [notebookSingleFile, setNotebookSingleFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [totalStorage, setTotalStorage] = useState(0);

  const [categoryName, setCategoryName] = useState("");
  const [categoryColor, setCategoryColor] = useState(FOLDER_COLORS[0]);
  const [editingCategory, setEditingCategory] = useState<FileFolder | null>(null);

  const [folderName, setFolderName] = useState("");
  const [folderColor, setFolderColor] = useState(FOLDER_COLORS[0]);
  const [editingFolder, setEditingFolder] = useState<FileFolder | null>(null);
  const [folderCategoryId, setFolderCategoryId] = useState<string>("");
  const [folderParentId, setFolderParentId] = useState<string | null>(null);

  const [currentFolderPath, setCurrentFolderPath] = useState<Record<string, FileFolder[]>>({});
  const [currentFolderData, setCurrentFolderData] = useState<Record<string, FileFolder | null>>({});
  const [folderFiles, setFolderFiles] = useState<Record<string, any[]>>({});
  const [folderSubFolders, setFolderSubFolders] = useState<Record<string, FileFolder[]>>({});

  const [movingFile, setMovingFile] = useState<FileItem | null>(null);
  const [allMoveTargets, setAllMoveTargets] = useState<FileFolder[]>([]);

  const [fileDetails, setFileDetails] = useState<ApiFileItem | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [editingPage, setEditingPage] = useState<any>(null);
  const [editedContent, setEditedContent] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [openingMaarifTest, setOpeningMaarifTest] = useState(false);
  const [maarifTemplateSettings, setMaarifTemplateSettings] = useState({
    choiceLayout: "vertical" as QuestionChoiceLayout,
    showIntroText: false,
  });

  useEffect(() => {
    refreshUserData();
  }, []);

  useEffect(() => {
    loadCategories();
  }, [refreshKey]);

  const loadCategories = async () => {
    try {
      const response = await ApiService.listCategories();
      setCategories(response.categories || []);
      setRootFolders(response.rootFolders || []);
    } catch (error) {
      console.error("Kategoriler yüklenemedi:", error);
    }
  };

  const loadAllMoveTargets = async () => {
    try {
      const response = await ApiService.listCategories();
      const targets: FileFolder[] = [];
      for (const cat of (response.categories || [])) {
        targets.push(cat);
        if (cat.folders) {
          for (const folder of cat.folders) {
            targets.push(folder);
            if (folder.subFolders) {
              for (const sub of folder.subFolders) {
                targets.push(sub);
              }
            }
          }
        }
      }
      setAllMoveTargets(targets);
    } catch (error) {
      console.error("Taşıma hedefleri yüklenemedi:", error);
    }
  };

  const navigateToFolder = async (tabId: string, folder: FileFolder) => {
    try {
      const folderData = await ApiService.getFolder(folder.id);
      const path = currentFolderPath[tabId] || [];
      setCurrentFolderPath(prev => ({ ...prev, [tabId]: [...path, folder] }));
      setCurrentFolderData(prev => ({ ...prev, [tabId]: folderData }));
      setFolderFiles(prev => ({ ...prev, [tabId]: folderData.files || [] }));
      setFolderSubFolders(prev => ({ ...prev, [tabId]: folderData.subFolders || [] }));
    } catch (error) {
      console.error("Klasör açılamadı:", error);
    }
  };

  const navigateBack = async (tabId: string) => {
    const path = currentFolderPath[tabId] || [];
    if (path.length <= 1) {
      setCurrentFolderPath(prev => ({ ...prev, [tabId]: [] }));
      setCurrentFolderData(prev => ({ ...prev, [tabId]: null }));
      setFolderFiles(prev => ({ ...prev, [tabId]: [] }));
      setFolderSubFolders(prev => ({ ...prev, [tabId]: [] }));
      setRefreshKey(prev => prev + 1);
    } else {
      const newPath = path.slice(0, -1);
      const parentFolder = newPath[newPath.length - 1];
      const folderData = await ApiService.getFolder(parentFolder.id);
      setCurrentFolderPath(prev => ({ ...prev, [tabId]: newPath }));
      setCurrentFolderData(prev => ({ ...prev, [tabId]: folderData }));
      setFolderFiles(prev => ({ ...prev, [tabId]: folderData.files || [] }));
      setFolderSubFolders(prev => ({ ...prev, [tabId]: folderData.subFolders || [] }));
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCreateCategory = async () => {
    if (!categoryName.trim()) {
      showAlert({ title: "Hata", message: "Kategori adı boş olamaz.", type: "error" });
      return;
    }

    try {
      await ApiService.createCategory({
        name: categoryName.trim(),
        color: categoryColor,
      });

      setShowCategoryModal(false);
      setCategoryName("");
      setCategoryColor(FOLDER_COLORS[0]);
      setRefreshKey(prev => prev + 1);

      showAlert({ title: "Başarılı", message: "Kategori başarıyla oluşturuldu.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Kategori oluşturulurken bir hata oluştu.", type: "error" });
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory || !categoryName.trim()) return;

    try {
      await ApiService.updateCategory(editingCategory.id, {
        name: categoryName.trim(),
        color: categoryColor,
      });

      setEditingCategory(null);
      setShowCategoryModal(false);
      setCategoryName("");
      setCategoryColor(FOLDER_COLORS[0]);
      setRefreshKey(prev => prev + 1);

      showAlert({ title: "Başarılı", message: "Kategori başarıyla güncellendi.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Kategori güncellenirken bir hata oluştu.", type: "error" });
    }
  };

  const handleDeleteCategory = async (category: FileFolder) => {
    const confirmed = await showConfirm({
      title: "Kategoriyi Sil",
      message: `"${category.name}" kategorisini silmek istediğinizden emin misiniz? İçindeki tüm klasörler ve dosyalar ana dizine taşınacaktır.`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      try {
        await ApiService.deleteCategory(category.id);
        setRefreshKey(prev => prev + 1);
        showAlert({ title: "Başarılı", message: "Kategori başarıyla silindi.", type: "success" });
      } catch (error: any) {
        showAlert({ title: "Hata", message: error.message || "Kategori silinemedi.", type: "error" });
      }
    }
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      showAlert({ title: "Hata", message: "Klasör adı boş olamaz.", type: "error" });
      return;
    }

    try {
      const payload: any = {
        name: folderName.trim(),
        color: folderColor,
        parentId: folderParentId,
      };
      if (folderCategoryId) {
        payload.categoryId = folderCategoryId;
      }

      await ApiService.createFolder(payload);

      setShowFolderModal(false);
      setFolderName("");
      setFolderColor(FOLDER_COLORS[0]);
      setFolderCategoryId("");
      setFolderParentId(null);
      setRefreshKey(prev => prev + 1);

      showAlert({ title: "Başarılı", message: "Klasör başarıyla oluşturuldu.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Klasör oluşturulurken bir hata oluştu.", type: "error" });
    }
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder || !folderName.trim()) return;

    try {
      await ApiService.updateFolder(editingFolder.id, {
        name: folderName.trim(),
        color: folderColor,
      });

      setEditingFolder(null);
      setShowFolderModal(false);
      setFolderName("");
      setFolderColor(FOLDER_COLORS[0]);
      setRefreshKey(prev => prev + 1);

      showAlert({ title: "Başarılı", message: "Klasör başarıyla güncellendi.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Klasör güncellenirken bir hata oluştu.", type: "error" });
    }
  };

  const handleDeleteFolder = async (folder: FileFolder) => {
    const confirmed = await showConfirm({
      title: "Klasörü Sil",
      message: `"${folder.name}" klasörünü silmek istediğinizden emin misiniz? İçindeki tüm dosyalar ve alt klasörler kalıcı olarak silinecektir.`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      try {
        await ApiService.deleteFolder(folder.id);

        setRefreshKey(prev => prev + 1);
        showAlert({ title: "Başarılı", message: "Klasör başarıyla silindi.", type: "success" });
      } catch (error: any) {
        showAlert({ title: "Hata", message: error.message || "Klasör silinemedi.", type: "error" });
      }
    }
  };

  const handleMoveFile = async (targetFolderId: string | null) => {
    if (!movingFile) return;

    try {
      await ApiService.moveFileToFolder(movingFile.id, targetFolderId);
      setShowMoveModal(false);
      setMovingFile(null);
      setRefreshKey(prev => prev + 1);

      showAlert({ title: "Başarılı", message: "Dosya başarıyla taşındı.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Dosya taşınamadı.", type: "error" });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["application/pdf"];
    if (!validTypes.includes(file.type)) {
      showAlert({ title: "Geçersiz Dosya Tipi", message: "Sadece PDF dosyaları yüklenebilir.", type: "error" });
      return;
    }

    if (totalStorage + file.size > MAX_STORAGE) {
      showAlert({ title: "Yetersiz Depolama Alanı", message: "Dosya yüklemek için yeterli depolama alanınız yok.", type: "error" });
      return;
    }

    setUploadFile(file);
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    try {
      setUploading(true);
      await ApiService.uploadFile(uploadFile);

      setShowUploadModal(false);
      setUploadFile(null);
      setRefreshKey((prev) => prev + 1);

      await refreshUserData();
      window.dispatchEvent(new CustomEvent("fileUsageUpdated"));

      showAlert({ title: "Başarılı", message: "Dosya başarıyla yüklendi.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Dosya yüklenirken bir hata oluştu.", type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleNotebookSingleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.notebook')) {
      showAlert({ title: 'Geçersiz Dosya', message: 'Lütfen .notebook uzantılı bir dosya seçin.', type: 'error' });

      return;
    };

    setNotebookSingleFile(file);
  };

  const handleNotebookUpload = async () => {
    if (!notebookSingleFile) return;
    try {
      setUploading(true);
      await ApiService.uploadNotebookFile(notebookSingleFile);

      setShowNotebookModal(false);
      setNotebookSingleFile(null);

      setRefreshKey((prev) => prev + 1);

      await refreshUserData();
      window.dispatchEvent(new CustomEvent("fileUsageUpdated"));

      showAlert({ title: "Başarılı", message: "Notebook başarıyla PDF'e dönüştürüldü ve yüklendi.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Notebook yüklenirken bir hata oluştu.", type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (file: FileItem) => {
    setSelectedFile(file);
    setShowPreviewModal(true);

    try {
      setLoadingDetails(true);

      const details = await ApiService.getFile(file.id);
      setFileDetails(details);
    } catch (error: any) {
      console.error("Failed to load file details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleOpenMaarifTest = (file: FileItem | ApiFileItem) => {
    const metadata = file.metadata;
    if (!isMaarifTestMetadata(metadata)) {
      showAlert({ title: "Hata", message: "Bağlı Dizgici taslağı bulunamadı.", type: "error" });
      return;
    }

    setSelectedMaarifFile(file);
    setMaarifTemplateSettings({
      choiceLayout: "vertical",
      showIntroText: false,
    });
    setShowMaarifTemplateModal(true);
  };

  const handleApplyMaarifTemplateAndOpen = async () => {
    const metadata = selectedMaarifFile?.metadata;
    if (!selectedMaarifFile || !isMaarifTestMetadata(metadata)) {
      showAlert({ title: "Hata", message: "Bağlı Dizgici taslağı bulunamadı.", type: "error" });
      return;
    }

    try {
      setOpeningMaarifTest(true);

      const questionBank = await ApiService.getQuestionBank(metadata.questionBankId);
      const currentContent = questionBank.content;
      const updatedQuestions = (currentContent?.sorular || []).map((question) =>
        applyMaarifTemplateToQuestion(question, maarifTemplateSettings)
      );

      await ApiService.updateQuestionBank(metadata.questionBankId, {
        content: {
          ...currentContent,
          sorular: updatedQuestions,
        },
      });

      setShowMaarifTemplateModal(false);
      setShowPreviewModal(false);
      navigate(`/question-bank-indesign/${metadata.questionBankId}/edit`);
    } catch (error: any) {
      showAlert({
        title: "Şablon uygulanamadı",
        message: error?.message || "Test Dizgici için hazırlanamadı.",
        type: "error",
      });
    } finally {
      setOpeningMaarifTest(false);
    }
  };

  const handleProcessFile = async () => {
    if (!selectedFile) return;

    try {
      setProcessing(true);
      await ApiService.processFile(selectedFile.id);

      const details = await ApiService.getFile(selectedFile.id);
      setFileDetails(details);

      showAlert({ title: "Başarılı", message: "Dosya başarıyla işlendi.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Dosya işlenirken bir hata oluştu.", type: "error" });
    } finally {
      setProcessing(false);
    }
  };

  const handleEditQuestion = (page: any) => {
    setEditingPage(page);
    setEditedContent(JSON.parse(JSON.stringify(page.content)));
    setShowEditModal(true);
  };

  const handleSaveQuestion = async () => {
    if (!selectedFile || !editedContent || !editingPage) return;

    try {
      setSaving(true);

      const updatePayload = [{
        pageNumber: editingPage.pageNumber,
        type: 'question',
        content: editedContent
      }];

      await ApiService.updateFileContent(selectedFile.id, updatePayload);

      const details = await ApiService.getFile(selectedFile.id);
      setFileDetails(details);

      setShowEditModal(false);
      setEditingPage(null);
      setEditedContent(null);

      showAlert({ title: "Başarılı", message: "Soru başarıyla güncellendi.", type: "success" });
    } catch (error: any) {
      showAlert({ title: "Hata", message: error.message || "Soru güncellenirken bir hata oluştu.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const downloadImage = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      showAlert({ title: "Hata", message: "Görsel indirilemedi.", type: "error" });
    }
  };

  const copyImageToClipboard = async (url: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ]);
      showAlert({ title: "Başarılı", message: "Görsel panoya kopyalandı.", type: "success" });
    } catch (error) {
      showAlert({ title: "Hata", message: "Görsel kopyalanamadı.", type: "error" });
    }
  };

  const handleDelete = async (file: FileItem) => {
    const confirmed = await showConfirm({
      title: "Dosyayı Sil",
      message: `"${file.name}" dosyasını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      confirmText: "Sil",
      confirmButtonVariant: "danger",
    });

    if (confirmed) {
      try {
        await ApiService.deleteFile(file.id);
        await refreshUserData();
        window.dispatchEvent(new CustomEvent("fileUsageUpdated"));
        setRefreshKey(prev => prev + 1);
        showAlert({ title: "Başarılı", message: "Dosya başarıyla silindi.", type: "success" });
      } catch (error: any) {
        showAlert({ title: "Hata", message: error.message || "Dosya silinirken bir hata oluştu.", type: "error" });
      }
    }
  };

  const renderFileItem = useCallback(
    (file: FileItem, viewMode: string = "list") => {
      if (viewMode === "grid") {
        return (
          <div
            key={file.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col h-full">
              <div className="flex items-center justify-center mb-3 h-32 bg-gray-50 rounded-lg overflow-hidden">
              {file.type === "image" ? (
                <img src={file.url} alt={file.name} className="w-full h-full object-contain" />
              ) : file.type === "maarif_test" ? (
                <div className="flex h-full w-full flex-col items-center justify-center bg-[linear-gradient(135deg,#eff6ff,#dbeafe)] text-blue-700">
                  <BookOpenIcon />
                  <span className="mt-2 text-xs font-semibold">Maarif Test</span>
                </div>
              ) : (
                <FileText className="w-16 h-16 text-red-500" />
              )}
            </div>

            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 truncate" title={file.name}>
                {file.name}
              </h3>
              {file.type === "maarif_test" && isMaarifTestMetadata(file.metadata) ? (
                <div className="mb-2 inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700">
                  {file.metadata.lessonTitle || "Maarif Test"}
                </div>
              ) : null}
              <div className="text-xs text-gray-500 space-y-1">
                <div className="flex items-center gap-1">
                  <span className="font-medium">Boyut:</span>
                  <span>{formatFileSize(file.size)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium">Tarih:</span>
                  <span>{formatDate(file.uploadedAt)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
              <button
                onClick={() => (file.type === "maarif_test" ? handleOpenMaarifTest(file) : handlePreview(file))}
                className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-800 rounded-lg hover:bg-blue-50 transition-colors font-medium text-sm flex-1">
                <Eye className="w-4 h-4" />
                {file.type === "maarif_test" ? "Dizgicide Aç" : "Önizle"}
              </button>
              {canUploadFile && (
                <button
                  onClick={() => {
                    setMovingFile(file);
                    loadAllMoveTargets();
                    setShowMoveModal(true);
                  }}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Taşı">
                  <Move className="w-4 h-4" />
                </button>
              )}
              {canDeleteFile && (
                <button
                  onClick={() => handleDelete(file)}
                  className="flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-800 rounded-lg hover:bg-red-50 transition-colors"
                  title="Sil">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        );
      }

      return (
        <div key={file.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow mb-2">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="flex-shrink-0 w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden">
                {file.type === "image" ? (
                  <img src={file.url} alt={file.name} className="w-full h-full object-cover" />
                ) : file.type === "maarif_test" ? (
                  <div className="flex h-full w-full items-center justify-center bg-blue-50 text-blue-700">
                    <BookOpenIcon small />
                  </div>
                ) : (
                  <FileText className="w-6 h-6 text-red-500" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate mb-1" title={file.name}>
                  {file.name}
                </h3>
                {file.type === "maarif_test" && isMaarifTestMetadata(file.metadata) ? (
                  <div className="mb-1">
                    <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                      {file.metadata.gradeLabel || "Maarif Test"} • {file.metadata.questionCount || 0} soru
                    </span>
                  </div>
                ) : null}
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>{formatFileSize(file.size)}</span>
                  <span>•</span>
                  <span>{formatDate(file.uploadedAt)}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-1 flex-shrink-0">
              <button
                onClick={() => (file.type === "maarif_test" ? handleOpenMaarifTest(file) : handlePreview(file))}
                className="flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50 transition-colors"
                title={file.type === "maarif_test" ? "Dizgicide Aç" : "Önizle"}>
                <Eye className="w-4 h-4" />
              </button>
              {canUploadFile && (
                <button
                  onClick={() => {
                    setMovingFile(file);
                    loadAllMoveTargets();
                    setShowMoveModal(true);
                  }}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 rounded-full hover:bg-gray-50 transition-colors"
                  title="Taşı">
                  <Move className="w-4 h-4" />
                </button>
              )}
              {canDeleteFile && (
                <button
                  onClick={() => handleDelete(file)}
                  className="flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50 transition-colors"
                  title="Sil">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      );
    },
    [handlePreview, handleDelete, handleOpenMaarifTest, canUploadFile, canDeleteFile]
  );

  const renderFolderCard = useCallback(
    (folder: FileFolder, categoryId: string, viewMode: string = "list") => {
      const hasSubFolders = !folder.parentId;
      const subCount = folder.subFolders?.length || 0;
      const fileCount = folder.files?.length || 0;
      const totalSize = folder.files?.reduce((sum: number, f: any) => sum + (f.size || 0), 0) || 0;

      if (viewMode === "grid") {
        return (
          <div
            key={folder.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow flex flex-col h-full group cursor-pointer"
            onClick={() => navigateToFolder(categoryId, folder)}
            style={{ borderTop: `3px solid ${folder.color}` }}
          >
            <div className="flex items-center justify-center mb-3 h-32 rounded-lg overflow-hidden"
              style={{ background: `linear-gradient(145deg, ${folder.color}12, ${folder.color}06)` }}
            >
              <Folder className="w-16 h-16" style={{ color: folder.color }} />
            </div>

            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 truncate" title={folder.name}>
                {folder.name}
              </h3>
              <div className="text-xs text-gray-500 space-y-1">
                <div className="flex items-center gap-1">
                  <span className="font-medium">Boyut:</span>
                  <span>{formatFileSize(totalSize)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium">Tarih:</span>
                  <span>{folder.createdAt ? formatDate(folder.createdAt) : '-'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="font-medium">Dosya:</span>
                  <span>{fileCount} dosya{subCount > 0 ? `, ${subCount} alt klasör` : ''}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToFolder(categoryId, folder);
                }}
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm flex-1"
                style={{ color: folder.color }}
              >
                <FolderOpen className="w-4 h-4" />
                Aç
              </button>
              {canUploadFile && hasSubFolders && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFolderCategoryId(categoryId);
                    setFolderParentId(folder.id);
                    setEditingFolder(null);
                    setFolderName("");
                    setFolderColor(FOLDER_COLORS[0]);
                    setShowFolderModal(true);
                  }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-50 transition-colors"
                  style={{ color: folder.color }}
                  title="Alt Klasör Oluştur">
                  <FolderPlus className="w-4 h-4" />
                </button>
              )}
              {canUploadFile && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingFolder(folder);
                    setFolderName(folder.name);
                    setFolderColor(folder.color);
                    setFolderCategoryId(categoryId);
                    setShowFolderModal(true);
                  }}
                  className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
                  title="Düzenle">
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              {canDeleteFile && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFolder(folder);
                  }}
                  className="flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-800 rounded-lg hover:bg-red-50 transition-colors"
                  title="Sil">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        );
      }

      return (
        <div
          key={folder.id}
          className="group cursor-pointer mb-2 rounded-xl overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
          onClick={() => navigateToFolder(categoryId, folder)}
          style={{
            background: `linear-gradient(135deg, ${folder.color}10, ${folder.color}05)`,
            border: `1.5px solid ${folder.color}25`,
          }}
        >
          <div className="flex items-center gap-4 p-4">

            <div className="relative flex-shrink-0">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${folder.color}25, ${folder.color}10)` }}
              >
                <Folder className="w-6 h-6" style={{ color: folder.color }} />
              </div>
              <div
                className="absolute -top-0.5 left-1 w-5 h-1.5 rounded-t-md"
                style={{ background: folder.color }}
              />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-gray-800 truncate">{folder.name}</h3>
              <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                <span>{formatFileSize(totalSize)}</span>
                <span>•</span>
                <span>{folder.createdAt ? formatDate(folder.createdAt) : '-'}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                {subCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full" style={{ background: `${folder.color}15`, color: folder.color }}>
                    <Folder className="w-3 h-3" />
                    {subCount} alt klasör
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  <FileText className="w-3 h-3" />
                  {fileCount} dosya
                </span>
              </div>
            </div>

            <div className="flex gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              {canUploadFile && hasSubFolders && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFolderCategoryId(categoryId);
                    setFolderParentId(folder.id);
                    setEditingFolder(null);
                    setFolderName("");
                    setFolderColor(FOLDER_COLORS[0]);
                    setShowFolderModal(true);
                  }}
                  className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
                  style={{ color: folder.color }}
                  title="Alt Klasör Oluştur">
                  <FolderPlus className="w-4 h-4" />
                </button>
              )}
              {canUploadFile && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingFolder(folder);
                    setFolderName(folder.name);
                    setFolderColor(folder.color);
                    setFolderCategoryId(categoryId);
                    setShowFolderModal(true);
                  }}
                  className="flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Düzenle">
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
              {canDeleteFile && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFolder(folder);
                  }}
                  className="flex items-center justify-center w-8 h-8 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  title="Sil">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
          </div>
        </div>
      );
    },
    [canUploadFile, canDeleteFile]
  );

  const renderCategoryContent = useCallback(
    (categoryId: string, item: any, viewMode: string = "list") => {
      if (item._isFolder) {
        const folder = item as FileFolder & { _isFolder: boolean };
        return renderFolderCard(folder, categoryId, viewMode);
      }

      return renderFileItem(item, viewMode);
    },
    [renderFileItem, renderFolderCard]
  );

  const renderRootContent = useCallback(
    (item: any, viewMode: string = "list") => {
      if (item._isFolder) {
        const folder = item as FileFolder & { _isFolder: boolean };
        return renderFolderCard(folder, "ana-dizin", viewMode);
      }
      return renderFileItem(item, viewMode);
    },
    [renderFileItem, renderFolderCard]
  );

  const mainTab: TabItemProps = useMemo(
    () => {
      const tabId = "ana-dizin";
      const path = currentFolderPath[tabId] || [];
      const isInFolder = path.length > 0;

      const mainActions = canUploadFile ? (
        <div className={LIST_TOOLBAR_ROW}>
          {!isInFolder && (
            <>
              <button
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryName("");
                  setCategoryColor(FOLDER_COLORS[0]);
                  setShowCategoryModal(true);
                }}
                className={`${LIST_TOOLBAR_BTN} bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600`}>
                <FolderPlus className={LIST_TOOLBAR_ICON} />
                <span>Yeni Kategori</span>
              </button>
              <button
                onClick={() => {
                  setFolderCategoryId("");
                  setFolderParentId(null);
                  setEditingFolder(null);
                  setFolderName("");
                  setFolderColor(FOLDER_COLORS[0]);
                  setShowFolderModal(true);
                }}
                className={`${LIST_TOOLBAR_BTN} bg-purple-600 text-white hover:bg-purple-700 border-purple-600`}>
                <FolderPlus className={LIST_TOOLBAR_ICON} />
                <span>Yeni Klasör</span>
              </button>
            </>
          )}
          {isInFolder && (
            <>
              <button
                onClick={() => {
                  setFolderCategoryId("");
                  setFolderParentId(path[path.length - 1].id);
                  setEditingFolder(null);
                  setFolderName("");
                  setFolderColor(FOLDER_COLORS[0]);
                  setShowFolderModal(true);
                }}
                className={`${LIST_TOOLBAR_BTN} bg-purple-600 text-white hover:bg-purple-700 border-purple-600`}>
                <FolderPlus className={LIST_TOOLBAR_ICON} />
                <span>Yeni Klasör</span>
              </button>
              <button
                onClick={() => navigateBack(tabId)}
                className={`${LIST_TOOLBAR_BTN} bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300`}>
                <ArrowLeft className={LIST_TOOLBAR_ICON} />
                <span>Geri</span>
              </button>
            </>
          )}
          <button
            onClick={() => setShowUploadModal(true)}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni PDF</span>
          </button>
          <button
            onClick={() => setShowNotebookModal(true)}
            className={`${LIST_TOOLBAR_BTN} bg-blue-600 text-white hover:bg-blue-700 border-blue-600`}>
            <FileText className={LIST_TOOLBAR_ICON} />
            <span>Yeni Notebook</span>
          </button>
        </div>
      ) : null;

      return {
        type: "grid" as const,
        title: isInFolder ? `Ana Dizin › ${path.map(p => p.name).join(' › ')}` : "Ana Dizin",
        path: "ana-dizin",
        tabId: "ana-dizin",
        actions: mainActions,
        isSearch: false,
        showToggle: true,
        item: (item: any, viewMode?: string) => renderRootContent(item, viewMode),
        emptyMessage: isInFolder
          ? "Bu klasörde henüz dosya yok."
          : "Ana dizinde henüz dosya veya klasör yok.",
        fetcher: async () => {
          if (isInFolder) {
            const currentFolder = path[path.length - 1];
            const folderData = await ApiService.getFolder(currentFolder.id);
            const subFolderItems = (folderData.subFolders || []).map((f: FileFolder) => ({ ...f, _isFolder: true }));
            const fileItems = (folderData.files || []).map((f: ApiFileItem) => mapApiFileToCard(f));
            return { data: [...subFolderItems, ...fileItems] };
          }

          const response = await ApiService.listCategories();
          const rootFolderItems = (response.rootFolders || []).map((f: FileFolder) => ({ ...f, _isFolder: true }));
          const fileItems = response.unassignedFiles || [];
          return { data: [...rootFolderItems, ...fileItems] };
        },
        dataProcessor: (data: any[]) => {
          if (isInFolder) return data;

          const result: any[] = [];

          for (const item of data) {
            if (item._isFolder) {
              result.push(item);
            } else {
              result.push(mapApiFileToCard(item));
            }
          }

          ApiService.listFiles(1, 1000).then(allFilesResponse => {
            const total = allFilesResponse.rows.reduce((sum, file) => sum + file.size, 0);
            setTotalStorage(total);
          });

          return result;
        },
        clientPageSize: 20,
        refreshKey: `files-root-${path.map(p => p.id).join('-')}-${refreshKey}`,
      };
    },
    [renderRootContent, refreshKey, currentFolderPath, canUploadFile]
  );

  const categoryTabs: TabItemProps[] = useMemo(
    () => categories.map((category) => {
      const tabId = category.id;
      const path = currentFolderPath[tabId] || [];
      const isInFolder = path.length > 0;

      const categoryActions = canUploadFile ? (
        <div className={LIST_TOOLBAR_ROW}>
          <button
            onClick={() => {
              setFolderCategoryId(tabId);
              setFolderParentId(isInFolder ? path[path.length - 1].id : null);
              setEditingFolder(null);
              setFolderName("");
              setFolderColor(FOLDER_COLORS[0]);
              setShowFolderModal(true);
            }}
            className={`${LIST_TOOLBAR_BTN} bg-purple-600 text-white hover:bg-purple-700 border-purple-600`}>
            <FolderPlus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Klasör</span>
          </button>
          {!isInFolder && (
            <>
              <button
                onClick={() => {
                  setEditingCategory(category);
                  setCategoryName(category.name);
                  setCategoryColor(category.color);
                  setShowCategoryModal(true);
                }}
                className={`${LIST_TOOLBAR_BTN} bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300`}>
                <Edit2 className={LIST_TOOLBAR_ICON} />
                <span>Düzenle</span>
              </button>
              <button
                onClick={() => handleDeleteCategory(category)}
                className={`${LIST_TOOLBAR_BTN} bg-red-50 text-red-600 hover:bg-red-100 border-red-200`}>
                <Trash2 className={LIST_TOOLBAR_ICON} />
                <span>Sil</span>
              </button>
            </>
          )}
          {isInFolder && (
            <button
              onClick={() => navigateBack(tabId)}
              className={`${LIST_TOOLBAR_BTN} bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300`}>
              <ArrowLeft className={LIST_TOOLBAR_ICON} />
              <span>Geri</span>
            </button>
          )}
          <button
            onClick={() => setShowUploadModal(true)}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni PDF</span>
          </button>
          <button
            onClick={() => setShowNotebookModal(true)}
            className={`${LIST_TOOLBAR_BTN} bg-blue-600 text-white hover:bg-blue-700 border-blue-600`}>
            <FileText className={LIST_TOOLBAR_ICON} />
            <span>Yeni Notebook</span>
          </button>
        </div>
      ) : null;

      return {
        type: "grid" as const,
        title: isInFolder ? `${category.name} › ${path.map(p => p.name).join(' › ')}` : category.name,
        path: category.id,
        tabId: category.id,
        actions: categoryActions,
        isSearch: false,
        showToggle: true,
        item: (item: any, viewMode?: string) => renderCategoryContent(tabId, item, viewMode),
        emptyMessage: isInFolder
          ? `Bu klasörde henüz dosya yok.`
          : `"${category.name}" kategorisinde henüz dosya veya klasör yok.`,
        fetcher: async () => {
          if (isInFolder) {
            const currentFolder = path[path.length - 1];
            const folderData = await ApiService.getFolder(currentFolder.id);
            const subFolderItems = (folderData.subFolders || []).map((f: FileFolder) => ({ ...f, _isFolder: true }));
            const fileItems = (folderData.files || []).map((f: ApiFileItem) => mapApiFileToCard(f));
            return { data: [...subFolderItems, ...fileItems] };
          }

          const categoryData = await ApiService.getCategory(category.id);
          const folderItems = (categoryData.folders || []).map((f: FileFolder) => ({ ...f, _isFolder: true }));
          const fileItems = (categoryData.files || []).map((f: ApiFileItem) => mapApiFileToCard(f));
          return { data: [...folderItems, ...fileItems] };
        },
        dataProcessor: (data: any[]) => data,
        clientPageSize: 50,
        refreshKey: `files-${category.id}-${path.map(p => p.id).join('-')}-${refreshKey}`,
      };
    }),
    [categories, renderFileItem, renderCategoryContent, refreshKey, currentFolderPath, canUploadFile, canDeleteFile]
  );

  const tabs = useMemo(() => [mainTab, ...categoryTabs], [mainTab, categoryTabs]);

  return (
    <>
      <TabPage title="Dosyalar" tabs={tabs} />

      <Modal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
          setCategoryName("");
          setCategoryColor(FOLDER_COLORS[0]);
        }}
        title={editingCategory ? "Kategoriyi Düzenle" : "Yeni Kategori Oluştur"}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kategori Adı</label>
            <input
              type="text"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="Örn: Matematik, Fizik..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kategori Rengi</label>
            <div className="flex flex-wrap gap-2">
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setCategoryColor(color)}
                  className={`w-8 h-8 rounded-lg transition-transform ${categoryColor === color ? "ring-2 ring-offset-2 ring-gray-400 scale-110" : "hover:scale-105"
                    }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowCategoryModal(false);
                setEditingCategory(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              İptal
            </button>
            <button
              onClick={editingCategory ? handleUpdateCategory : handleCreateCategory}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
            >
              {editingCategory ? "Kaydet" : "Oluştur"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showFolderModal}
        onClose={() => {
          setShowFolderModal(false);
          setEditingFolder(null);
          setFolderName("");
          setFolderColor(FOLDER_COLORS[0]);
        }}
        title={editingFolder ? "Klasörü Düzenle" : "Yeni Klasör Oluştur"}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Klasör Adı</label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Örn: Sorular, Sunumlar..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Klasör Rengi</label>
            <div className="flex flex-wrap gap-2">
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color}
                  onClick={() => setFolderColor(color)}
                  className={`w-8 h-8 rounded-lg transition-transform ${folderColor === color ? "ring-2 ring-offset-2 ring-gray-400 scale-110" : "hover:scale-105"
                    }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowFolderModal(false);
                setEditingFolder(null);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              İptal
            </button>
            <button
              onClick={editingFolder ? handleUpdateFolder : handleCreateFolder}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
            >
              {editingFolder ? "Kaydet" : "Oluştur"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setMovingFile(null);
        }}
        title="Dosyayı Taşı"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            <strong>{movingFile?.name}</strong> dosyasını nereye taşımak istiyorsunuz?
          </p>

          <div className="max-h-64 overflow-y-auto space-y-2">
            <button
              onClick={() => handleMoveFile(null)}
              className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 transition-colors ${!movingFile?.folderId ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:bg-gray-50"
                }`}
            >
              <Home className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium">Ana Dizin</span>
            </button>

            {allMoveTargets.map((target: FileFolder) => (
              <button
                key={target.id}
                onClick={() => handleMoveFile(target.id)}
                className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 transition-colors ${movingFile?.folderId === target.id ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:bg-gray-50"
                  }`}
                style={{ paddingLeft: target.type === 'folder' && target.parentId ? '2.5rem' : target.type === 'folder' ? '1.5rem' : undefined }}
              >
                {target.type === 'category' ? (
                  <Folder className="w-5 h-5" style={{ color: target.color }} />
                ) : (
                  <FolderOpen className="w-5 h-5" style={{ color: target.color }} />
                )}
                <div>
                  <span className="text-sm font-medium">{target.name}</span>
                  <span className="text-xs text-gray-400 ml-1">
                    {target.type === 'category' ? '(Kategori)' : '(Klasör)'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadFile(null);
        }}
        title="Yeni PDF Yükle"
        size="md">
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-yellow-800 mb-1">Önemli Uyarılar (İşleme Yapılacaksa)</h4>
                <ul className="text-xs text-yellow-700 space-y-1 list-disc list-inside">
                  <li>PDF en az 2, en fazla 10 sayfa olmalıdır.</li>
                  <li>Her sayfada tek bir soru ya da cevap olmalıdır.</li>
                  <li>Her soru sayfasından sonraki sayfa o sorunun çözümü olmalıdır.</li>
                  <li>Sayfaların yatay olması daha iyi sonuç verir.</li>
                  <li>Kareli defter veya desenli kağıt yerine düz beyaz kağıt kullanılmalıdır.</li>
                  <li>Grafik, şekil veya görsel içeren sorular sistem tarafından okunamaz.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-blue-700">
                  PDF işleme özelliği <span className="font-semibold">Deep</span> tarafından sağlanmaktadır.
                  <br />
                  Deep hata yapabilir, önemli bilgileri kontrol edin.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Dosya Seç</label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            <p className="text-xs text-gray-500 mt-2">
              Sadece PDF dosyaları kabul edilir. (Maks: {formatFileSize(MAX_STORAGE - totalStorage)} Kalan)
            </p>
          </div>

          {uploadFile && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <FileText className="w-8 h-8 text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{uploadFile.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(uploadFile.size)}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowUploadModal(false);
                setUploadFile(null);
              }}
              disabled={uploading}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              İptal
            </button>
            <button
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
              {uploading ? "Yükleniyor..." : "Yükle"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showNotebookModal}
        onClose={() => {
          setShowNotebookModal(false);
          setNotebookSingleFile(null);
        }}
        title="Notebook'tan PDF Oluştur"
        size="md">
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              <strong>.notebook</strong> dosyasını doğrudan seçin. Sistem otomatik olarak içindeki sayfaları çıkarıp PDF'e dönüştürecektir.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">.notebook Dosyası Seç</label>
            <input
              type="file"
              accept=".notebook"
              onChange={handleNotebookSingleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {notebookSingleFile && (
            <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{notebookSingleFile.name}</p>
                <p className="text-xs text-gray-500">{(notebookSingleFile.size / 1024 / 1024).toFixed(1)} MB</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowNotebookModal(false);
                setNotebookSingleFile(null);
              }}
              disabled={uploading}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              İptal
            </button>
            <button
              onClick={handleNotebookUpload}
              disabled={!notebookSingleFile || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
              {uploading ? "Dönüştürülüyor..." : "PDF'e Dönüştür"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showMaarifTemplateModal}
        onClose={() => {
          setShowMaarifTemplateModal(false);
          setSelectedMaarifFile(null);
        }}
        title="Dizgiciye Aktar"
        size="md">
        <div className="space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4">
            <div className="text-sm font-semibold text-amber-950">Soru Düzeni</div>
            <div className="text-xs text-amber-900/70 mt-1 mb-3">
              Bu seçimler kaydedilmiş Maarif testindeki tüm sorulara uygulanır. Soru sayısı değişmez.
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-900/80 mb-2">Üst Açıklama</div>
                <div className="text-xs text-amber-900/70 mb-2">Sadece görselli sorular için uygulanır.</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setMaarifTemplateSettings((prev) => ({ ...prev, showIntroText: false }))}
                    className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                      !maarifTemplateSettings.showIntroText
                        ? "border-blue-500 bg-white text-blue-900"
                        : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                    }`}>
                    <div className="font-semibold">Yok</div>
                    <div className="mt-1 text-xs text-gray-500">Görselli sorularda üst açıklama alanı kapalı olsun.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMaarifTemplateSettings((prev) => ({ ...prev, showIntroText: true }))}
                    className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                      maarifTemplateSettings.showIntroText
                        ? "border-blue-500 bg-white text-blue-900"
                        : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                    }`}>
                    <div className="font-semibold">Olsun</div>
                    <div className="mt-1 text-xs text-gray-500">Görselli sorularda üst açıklama alanı açık olsun.</div>
                  </button>
                </div>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-amber-900/80 mb-2">Şık Yerleşimi</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setMaarifTemplateSettings((prev) => ({ ...prev, choiceLayout: "vertical" }))}
                    className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                      maarifTemplateSettings.choiceLayout === "vertical"
                        ? "border-blue-500 bg-white text-blue-900"
                        : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                    }`}>
                    <div className="font-semibold">Alt Alta</div>
                    <div className="mt-1 text-xs text-gray-500">Klasik şık dizilimi.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMaarifTemplateSettings((prev) => ({ ...prev, choiceLayout: "horizontal" }))}
                    className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                      maarifTemplateSettings.choiceLayout === "horizontal"
                        ? "border-blue-500 bg-white text-blue-900"
                        : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                    }`}>
                    <div className="font-semibold">Yatay</div>
                    <div className="mt-1 text-xs text-gray-500">Şıklar yan yana dizilsin.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMaarifTemplateSettings((prev) => ({ ...prev, choiceLayout: "staggered" }))}
                    className={`rounded-xl border px-3 py-3 text-left text-sm transition ${
                      maarifTemplateSettings.choiceLayout === "staggered"
                        ? "border-blue-500 bg-white text-blue-900"
                        : "border-amber-200 bg-white/80 text-gray-700 hover:bg-white"
                    }`}>
                    <div className="font-semibold">Çapraz</div>
                    <div className="mt-1 text-xs text-gray-500">Şıklar kademeli yerleşsin.</div>
                  </button>
                </div>
              </div>

            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              onClick={() => {
                setShowMaarifTemplateModal(false);
                setSelectedMaarifFile(null);
              }}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm">
              Vazgeç
            </button>
            <button
              onClick={handleApplyMaarifTemplateAndOpen}
              disabled={openingMaarifTest}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 text-sm font-medium">
              {openingMaarifTest ? "Hazırlanıyor..." : "Dizgicide Aç"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedFile(null);
          setFileDetails(null);
        }}
        title={
          selectedFile
            ? `${selectedFile.name} • ${formatFileSize(selectedFile.size)} • ${formatDate(selectedFile.uploadedAt)}`
            : "Dosya Önizleme"
        }
        size="xl">
        {selectedFile && (
          selectedFile.type === "maarif_test" && isMaarifTestMetadata(fileDetails?.metadata || selectedFile.metadata) ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
                <div className="flex items-start justify-between gap-4 flex-col md:flex-row">
                  <div>
                    <h4 className="text-base font-semibold text-blue-900">Maarif Test kaydı</h4>
                    <p className="mt-1 text-sm text-blue-800">
                      Bu kayıt PDF değil, doğrudan Dizgici taslağına bağlı yapılandırılmış bir testtir.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-blue-900">
                      <span className="rounded-full bg-white px-3 py-1 border border-blue-200">
                        Ders: {(fileDetails?.metadata as MaarifTestFileMetadata)?.lessonTitle || "-"}
                      </span>
                      <span className="rounded-full bg-white px-3 py-1 border border-blue-200">
                        Sınıf: {(fileDetails?.metadata as MaarifTestFileMetadata)?.gradeLabel || "-"}
                      </span>
                      <span className="rounded-full bg-white px-3 py-1 border border-blue-200">
                        Zorluk: {(fileDetails?.metadata as MaarifTestFileMetadata)?.difficulty || "-"}
                      </span>
                      <span className="rounded-full bg-white px-3 py-1 border border-blue-200">
                        Soru: {(fileDetails?.metadata as MaarifTestFileMetadata)?.questionCount || 0}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleOpenMaarifTest(fileDetails || selectedFile)}
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    <Eye className="h-4 w-4" />
                    Dizgicide Aç
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="text-sm font-semibold text-slate-900">Konu</div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {(fileDetails?.metadata as MaarifTestFileMetadata)?.topic || "Konu bilgisi bulunmuyor."}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className={`rounded-lg p-4 border-l-4 ${fileDetails?.status === "PROCESSED"
                  ? "bg-green-50 border-green-500"
                  : fileDetails?.status === "FAILED"
                    ? "bg-red-50 border-red-500"
                    : "bg-yellow-50 border-yellow-500"
                  }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {fileDetails?.status === "PROCESSED" ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : fileDetails?.status === "FAILED" ? (
                        <XCircle className="w-5 h-5 text-red-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-yellow-600" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">
                        {fileDetails?.status === "PROCESSED"
                          ? "İşlenmiş"
                          : fileDetails?.status === "FAILED"
                            ? "İşleme Başarısız"
                            : "İşlenmemiş"}
                      </h4>
                      <p className="text-xs mt-1 text-gray-600">
                        {fileDetails?.status === "PROCESSED"
                          ? `Dosya başarıyla işlendi ve ${fileDetails.pages?.length || 0} soru çıkarıldı.`
                          : fileDetails?.status === "FAILED"
                            ? "Dosya işlenirken bir hata oluştu."
                            : "Dosya henüz işlenmedi. İşlemek için butona tıklayın."}
                      </p>
                      <p className="text-xs mt-2 text-gray-500 italic">
                        PDF işleme özelliği <span className="font-semibold">Deep</span> tarafından sağlanmaktadır. Deep hata yapabilir, önemli
                        bilgileri kontrol edin.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {fileDetails?.status === "PROCESSED" && (
                      <button
                        onClick={() => {
                          setShowQuestionsModal(true);
                          setShowPreviewModal(false);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Soruları Görüntüle
                      </button>
                    )}
                    {fileDetails?.status !== "PROCESSED" && canProcessFile && (
                      <button
                        onClick={handleProcessFile}
                        disabled={processing || loadingDetails}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                        {processing && <Loader2 className="w-4 h-4 animate-spin" />}
                        {processing ? "İşleniyor..." : "İşle"}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center min-h-[400px]">
                {selectedFile.type === "image" ? (
                  <img src={selectedFile.url} alt={selectedFile.name} className="max-w-full max-h-[600px] object-contain" />
                ) : (
                  <iframe src={selectedFile.url} className="w-full h-[600px] border-0 rounded" title={selectedFile.name} />
                )}
              </div>
            </div>
          )
        )}
      </Modal>

      <Modal
        isOpen={showQuestionsModal}
        onClose={() => {
          setShowQuestionsModal(false);
        }}
        title={`Sorular (${fileDetails?.pages?.length || 0})`}
        size="xl">
        {fileDetails?.pages && fileDetails.pages.length > 0 ? (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {fileDetails.pages.map((page) => (
              <div key={page.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <h5 className="font-medium">Soru {page.content?.question_number || page.pageNumber}</h5>
                  {canProcessFile && (
                    <button
                      onClick={() => handleEditQuestion(page)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">Düzenle</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {page.processedImageUrl && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Soru:</p>
                      <img
                        src={page.processedImageUrl}
                        alt={`Soru ${page.pageNumber}`}
                        className="w-full h-auto max-h-[300px] object-contain rounded border border-gray-200 mb-2"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            downloadImage(page.processedImageUrl!, `soru-${page.content?.question_number || page.pageNumber}.png`)
                          }
                          className="flex items-center justify-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex-1"
                          title="Soruyu İndir">
                          <Download className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">İndir</span>
                        </button>
                        <button
                          onClick={() => copyImageToClipboard(page.processedImageUrl!)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex-1"
                          title="Soruyu Kopyala">
                          <Copy className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">Kopyala</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {page.content?.solution?.imageUrl && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Çözüm:</p>
                      <img
                        src={page.content.solution.imageUrl}
                        alt="Çözüm"
                        className="w-full h-auto max-h-[300px] object-contain rounded border border-gray-200 mb-2"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() =>
                            downloadImage(
                              page.content!.solution!.imageUrl!,
                              `cevap-${page.content?.question_number || page.pageNumber}.png`
                            )
                          }
                          className="flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition-colors flex-1"
                          title="Cevabı İndir">
                          <Download className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">İndir</span>
                        </button>
                        <button
                          onClick={() => copyImageToClipboard(page.content!.solution!.imageUrl!)}
                          className="flex items-center justify-center gap-1.5 px-3 py-2 bg-pink-600 text-white rounded-lg text-sm font-medium hover:bg-pink-700 transition-colors flex-1"
                          title="Cevabı Kopyala">
                          <Copy className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">Kopyala</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>Henüz işlenmiş soru bulunmuyor.</p>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPage(null);
          setEditedContent(null);
        }}
        title={`Soru ${editedContent?.question_number || ""} Düzenle`}
        size="lg">
        {editedContent && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Soru Metni</label>
              <textarea
                value={editedContent.question_text || ""}
                onChange={(e) => setEditedContent({ ...editedContent, question_text: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grafik Açıklaması (Opsiyonel)</label>
              <textarea
                value={editedContent.graph_description || ""}
                onChange={(e) => setEditedContent({ ...editedContent, graph_description: e.target.value })}
                rows={2}
                placeholder="Soruda grafik, şekil veya görsel varsa açıklayın..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {editedContent.main_formula_latex !== undefined && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ana Formül (LaTeX)</label>
                <input
                  type="text"
                  value={editedContent.main_formula_latex || ""}
                  onChange={(e) => setEditedContent({ ...editedContent, main_formula_latex: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

            {editedContent.options && editedContent.options.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Şıklar</label>
                <div className="space-y-2">
                  {editedContent.options.map((option: any, index: number) => (
                    <div key={option.label} className="flex items-start gap-2">
                      <span className="font-medium text-gray-700 mt-2">{option.label})</span>
                      <input
                        type="text"
                        value={option.content || ""}
                        onChange={(e) => {
                          const newOptions = [...editedContent.options];
                          newOptions[index] = { ...option, content: e.target.value };
                          setEditedContent({ ...editedContent, options: newOptions });
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {editedContent.correct_answer !== undefined && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Doğru Cevap</label>
                <select
                  value={editedContent.correct_answer || ""}
                  onChange={(e) => setEditedContent({ ...editedContent, correct_answer: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Seçiniz</option>
                  {editedContent.options?.map((option: any) => (
                    <option key={option.label} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {editedContent.solution && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Çözüm</label>
                <textarea
                  value={editedContent.solution.content || ""}
                  onChange={(e) =>
                    setEditedContent({
                      ...editedContent,
                      solution: { ...editedContent.solution, content: e.target.value },
                    })
                  }
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingPage(null);
                  setEditedContent(null);
                }}
                disabled={saving}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                İptal
              </button>
              <button
                onClick={handleSaveQuestion}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
